// sim/territory.ts — the TERRITORY SYSTEM: how reached regions are GRADUALLY
// turned into useful, productive provinces. The RegionManager (regions.ts) flips
// a binary "unlocked" bit when the frontier reaches a zone — but a freshly-
// scouted zone is only EXPLORED, not yet useful. This layer marches each reached
// region up a five-rung ladder of stages
//   Explored → Claimed → Settled → Established → Integrated
// by spending WORKER LABOR. Benefits unlock gradually per stage: food yield ramps
// 0%→35%→70%→100%→130%, gene drops switch on at Settled, and the rarity bias
// drifts upward as a province matures. Empire-building, not a switch.
//
// PER-(COLONY, REGION). Establishment is tracked independently FOR EACH COLONY:
// every colony runs its OWN five-rung ladder over the same shared regions, so a
// rival can settle the Garden while the player has barely Claimed it. State is a
// flat SoA indexed by colony THEN region — stage/idleSteps at
// `cid * REGION_COUNT + r`, effort at `(cid * REGION_COUNT + r) * ESTABLISH_WORK_COUNT
// + work`. Every method takes a leading `cid`; all semantics (thresholds, the
// food-throughput gate, abandonment decay, hysteresis) are preserved per colony.
//
// Why a stage gates on ONE labor: each rung consumes a DIFFERENT one of the four
// establishment labors (Trail → Logistics → FoodTransport → RouteReinforce), held
// in separate effort channels. A region only advances when it has banked enough
// effort in the CURRENT stage's channel; effort in any other channel does not
// count. So all four labors are mandatory across the arc, in order. The
// FoodTransport rung additionally caps its progress on actual food THROUGHPUT —
// you cannot "establish" a supply line with no food flowing through it.
//
// Decoupling: this system OBSERVES a RegionManager passed per-call (it is never
// stored), exactly how the region layer stays independent of the colony systems.
// Determinism: pure integer SoA state, fixed iteration order, no allocation in
// the per-call paths, no RNG (establishment is a deterministic function of the
// labor invested). The state hashes to a 32-bit fingerprint for the world hash.

import {
  NIL,
  TERRITORY_STAGE,
  ESTABLISH_WORK,
  ESTABLISH_WORK_COUNT,
} from '../types.ts';
import type { ColonyId } from '../types.ts';
import { fnvInit, fnvU32 } from '../hash.ts';
import { REGION_COUNT } from './regions.ts';
import type { Region, RegionManager } from './regions.ts';
import {
  STAGE_WORK,
  STAGE_REQUIREMENT,
  STAGE_YIELD_PCT,
  STAGE_GENE_ACCESS,
  STAGE_RARITY_DELTA,
  DEFAULT_TERRITORY_DECAY,
} from './territoryData.ts';
import type { TerritoryDecayConfig } from './territoryData.ts';

export type { TerritoryDecayConfig };
export { DEFAULT_TERRITORY_DECAY, SLOW_TERRITORY_DECAY } from './territoryData.ts';

/**
 * Labor productivity knob: effort banked per establishment step is
 * `1 + floor(laborForce / LABOR_DIVISOR)` (≥ 1 whenever any labor is dedicated).
 * Tuned — NOT an arbitrary round number — against the real colony growth curve:
 *   • A fledgling (~10 workers+specialists) banks 3 effort/step, so the four
 *     rungs (STAGE_REQUIREMENT 20/40/70/110) are a long, visible haul it cannot
 *     rush, while a mature colony (40 labor ⇒ 11/step) integrates a zone in a
 *     few dozen steps. The divisor sets that fledgling-vs-mature spread.
 *   • The constant `+1` floor guarantees a colony with *any* spare labor always
 *     makes forward progress (never a zero step), which the wrong-labor-gate and
 *     small-steps determinism tests rely on.
 * The tests pin this arithmetic; change it only with a growth-curve rationale
 * and a matching test update.
 */
export const LABOR_DIVISOR = 4;
/**
 * Food-line throttle: on the FoodTransport rung, effort is capped at
 * `1 + floor(foodThroughput / THROUGHPUT_DIVISOR)` so a supply line cannot be
 * established with no food flowing. Tuned to bite a little harder than labor
 * (6 > LABOR_DIVISOR 4) so the Settled→Established step is genuinely
 * food-gated, not labor-gated — moving 24 food/step only yields 5 effort, well
 * under the 11 a 40-strong labor force would otherwise bank. Defensible; kept.
 */
export const THROUGHPUT_DIVISOR = 6;

export class TerritorySystem {
  /** Number of colonies the SoA is sized for (the outer index dimension). */
  readonly maxColonies: number;
  /**
   * Current establishment stage per (colony, region), TERRITORY_STAGE.*,
   * indexed by `cid * REGION_COUNT + r`.
   */
  private readonly stage: Uint8Array;
  /**
   * Banked effort per (colony, region, work) channel:
   * index `(cid * REGION_COUNT + r) * ESTABLISH_WORK_COUNT + work`.
   */
  private readonly effort: Int32Array;
  /** Total stage advancements ever made across ALL colonies (monotonic; folded into the hash). */
  advancementsTotal = 0;
  /** Total stage DEMOTIONS ever made across ALL colonies (monotonic; folded into the hash when decay is on). */
  demotionsTotal = 0;

  // --- Abandonment-decay state (inert unless decay is enabled) ---------------
  /** Decay tuning (effort drain / demotion of a neglected region). Frozen at construction. */
  private readonly decay: TerritoryDecayConfig;
  /** True iff `decay.abandonDecay > 0` — the master switch for the whole mechanism. */
  private readonly decayEnabled: boolean;
  /**
   * Consecutive establishment steps each (colony, region) has gone WITHOUT receiving
   * effort. Only advanced/read when decay is enabled; stays all-zero (and unhashed)
   * otherwise, so a no-decay system is byte-identical to one without this field.
   */
  private readonly idleSteps: Int32Array;
  /**
   * Monotonic establishment-step clock PER COLONY; decay fires for colony `cid`
   * when `decayClock[cid] % decayInterval === 0`. Per-colony so one colony's
   * establishment cadence cannot perturb another's decay timing.
   */
  private readonly decayClock: Int32Array;

  constructor(maxColonies: number, decay: TerritoryDecayConfig = DEFAULT_TERRITORY_DECAY) {
    // At least one colony slot so the arrays are always well-formed.
    this.maxColonies = Math.max(1, Math.trunc(maxColonies));
    const cells = this.maxColonies * REGION_COUNT;
    // All (colony, region) cells begin Unexplored (0) with every effort channel empty.
    this.stage = new Uint8Array(cells);
    this.effort = new Int32Array(cells * ESTABLISH_WORK_COUNT);
    this.idleSteps = new Int32Array(cells);
    this.decayClock = new Int32Array(this.maxColonies);
    // Sanitise the config into well-defined integer bounds (caller can't smuggle
    // a negative interval / out-of-range floor into the deterministic state).
    const abandonDecay = Math.max(0, Math.trunc(decay.abandonDecay));
    this.decayEnabled = abandonDecay > 0;
    this.decay = {
      abandonDecay,
      decayInterval: Math.max(1, Math.trunc(decay.decayInterval)),
      idleGraceSteps: Math.max(0, Math.trunc(decay.idleGraceSteps)),
      demoteFloor: Math.min(
        TERRITORY_STAGE.Established,
        Math.max(TERRITORY_STAGE.Explored, Math.trunc(decay.demoteFloor)),
      ),
      demoteHoldPct: Math.min(99, Math.max(0, Math.trunc(decay.demoteHoldPct))),
    };
  }

  // --- Index helpers (O(1), allocation-free) ---------------------------------

  /** Flat (colony, region) cell index for the stage/idleSteps arrays. */
  private cell(cid: number, r: number): number {
    return cid * REGION_COUNT + r;
  }

  /** Flat (colony, region, work) index for the effort array. */
  private effortIdx(cid: number, r: number, work: number): number {
    return (cid * REGION_COUNT + r) * ESTABLISH_WORK_COUNT + work;
  }

  // --- Exploration sync (frontier → ladder entry) ----------------------------

  /**
   * Promote every newly-unlocked region from Unexplored to Explored FOR COLONY
   * `cid` — the moment the frontier reaches it, it joins that colony's
   * establishment ladder at the bottom. Never downgrades (an already-advanced
   * region keeps its stage). Returns the count of regions newly marked Explored.
   * Deterministic, allocation-free.
   */
  syncExploration(cid: ColonyId, mgr: RegionManager): number {
    let n = 0;
    const base = cid * REGION_COUNT;
    for (let r = 0; r < REGION_COUNT; r++) {
      if ((this.stage[base + r] as number) !== TERRITORY_STAGE.Unexplored) continue;
      if (!mgr.isUnlocked(r as Region)) continue;
      this.stage[base + r] = TERRITORY_STAGE.Explored;
      n++;
    }
    return n;
  }

  // --- Target selection + advancement ---------------------------------------

  /**
   * The region colony `cid` is currently establishing: the LOWEST-stage region
   * among those still in progress (stage in [Explored, Established]), tie-broken
   * by the LOWEST region index. Concentrating labor on one frontier zone at a
   * time is the "march outward" pacing. Returns NIL (-1) if none is in progress.
   */
  private activeTarget(cid: number): number {
    let best = NIL;
    let bestStage = 0;
    const base = cid * REGION_COUNT;
    for (let r = 0; r < REGION_COUNT; r++) {
      const s = this.stage[base + r] as number;
      if (s < TERRITORY_STAGE.Explored || s >= TERRITORY_STAGE.Integrated) continue;
      if (best === NIL || s < bestStage) {
        best = r;
        bestStage = s;
      }
    }
    return best;
  }

  /**
   * Advance colony `cid`'s region `r` as far as its banked effort allows. A region
   * leaves its stage once the CURRENT stage's work channel holds at least that
   * stage's requirement. In practice this advances at most one stage per call: the
   * next stage reads a different (empty) channel, so it immediately stalls — that
   * gap is the intended gradual pacing. Returns the number of stages advanced.
   */
  private tryAdvance(cid: number, r: number): number {
    let advanced = 0;
    const c = this.cell(cid, r);
    const ebase = c * ESTABLISH_WORK_COUNT;
    for (;;) {
      const s = this.stage[c] as number;
      if (s < TERRITORY_STAGE.Explored || s > TERRITORY_STAGE.Established) break;
      const work = STAGE_WORK[s] as number;
      const need = STAGE_REQUIREMENT[s] as number;
      if ((this.effort[ebase + work] as number) < need) break;
      // Banked enough in this rung's channel — climb one stage (capped at Integrated).
      this.stage[c] = (s + 1) as number;
      this.advancementsTotal++;
      advanced++;
    }
    return advanced;
  }

  /**
   * The effort threshold below which a region DEMOTES out of its current stage,
   * with hysteresis. A region leaves a stage when its current channel falls to
   * (or below) zero — i.e. the whole rung has bled away. (Demotion is "all the
   * way to empty" rather than "below the entry bar" so that, combined with the
   * refill-on-demotion below, a stage boundary cannot thrash: a demoted region
   * lands MID-rung, far from both the promote bar above and the demote floor.)
   */
  private demoteThreshold(): number {
    return 0;
  }

  /**
   * Slow ABANDONMENT DECAY for every region of colony `cid` that did NOT receive
   * effort this step (`fedRegion` is the one that did, or NIL). No-op unless decay
   * is enabled.
   *
   * Each idle region's miss-counter ticks up; once it has been silent for longer
   * than `idleGraceSteps`, and on the `decayInterval` cadence, it bleeds
   * `abandonDecay` effort from its CURRENT stage's channel. When that channel
   * empties (and the region sits above `demoteFloor`) the region DEMOTES one
   * stage: the lower stage's channel is refilled to `demoteHoldPct` of its
   * requirement (hysteresis — the region rests mid-rung, not on the boundary)
   * and its idle clock resets so a fresh grace window precedes any further slip.
   *
   * Integrated has no working channel of its own, so the first qualifying decay
   * event on an abandoned Integrated province demotes it directly to Established
   * and seeds that channel — abandonment reaches even a "finished" province.
   *
   * Deterministic, integer-only, allocation-free, fixed region order. The decay
   * cadence clock is PER COLONY.
   */
  private decayStep(cid: number, fedRegion: number): void {
    if (!this.decayEnabled) return;
    // Advance this colony's step clock once per establishment step; decay only on cadence.
    const clock = (this.decayClock[cid] as number) + 1;
    this.decayClock[cid] = clock;
    const onCadence = clock % this.decay.decayInterval === 0;
    const floor = this.decay.demoteFloor;
    const cbase = cid * REGION_COUNT;
    for (let r = 0; r < REGION_COUNT; r++) {
      const c = cbase + r;
      const s = this.stage[c] as number;
      // Regions not on the ladder, or already at/below the demote floor, never decay.
      if (s <= floor || s > TERRITORY_STAGE.Integrated) {
        this.idleSteps[c] = 0;
        continue;
      }
      if (r === fedRegion) {
        // Active labor this step — reset the idle clock, no decay.
        this.idleSteps[c] = 0;
        continue;
      }
      const idle = (this.idleSteps[c] as number) + 1;
      this.idleSteps[c] = idle;
      if (!onCadence || idle <= this.decay.idleGraceSteps) continue;

      if (s === TERRITORY_STAGE.Integrated) {
        // No banked effort at the top rung — abandonment knocks it down to
        // Established and seeds that channel to the hysteresis hold.
        this.demoteRegion(cid, r, TERRITORY_STAGE.Integrated);
        continue;
      }
      const work = STAGE_WORK[s] as number;
      const idx = c * ESTABLISH_WORK_COUNT + work;
      const have = (this.effort[idx] as number) - this.decay.abandonDecay;
      if (have > this.demoteThreshold()) {
        this.effort[idx] = have; // bled some effort, still holds the stage
      } else {
        this.effort[idx] = 0; // rung exhausted — drop a stage
        this.demoteRegion(cid, r, s);
      }
    }
  }

  /**
   * Demote colony `cid`'s region `r` one stage down from `from` (clamped at
   * `demoteFloor`), seeding the now-current (lower) stage's channel to
   * `demoteHoldPct` of its requirement so the region rests mid-rung (hysteresis)
   * and resetting its idle clock. Folded counters keep the demotion in the hash.
   */
  private demoteRegion(cid: number, r: number, from: number): void {
    const to = Math.max(this.decay.demoteFloor, from - 1);
    if (to >= from) return; // already at/below the floor — nothing to do
    const c = this.cell(cid, r);
    this.stage[c] = to;
    this.demotionsTotal++;
    this.idleSteps[c] = 0;
    // Seed the re-entered stage's channel for hysteresis (0 when at the floor —
    // Explored has no requirement, so it is simply re-earned from scratch).
    const work = STAGE_WORK[to] as number;
    const need = STAGE_REQUIREMENT[to] as number;
    const idx = c * ESTABLISH_WORK_COUNT + work;
    this.effort[idx] = need > 0 ? Math.floor((need * this.decay.demoteHoldPct) / 100) : 0;
  }

  /**
   * ONE establishment cadence step FOR COLONY `cid` — the empire-building
   * heartbeat. Folds the frontier in (syncExploration), picks the single active
   * target, and pours this step's labor into that target's current work channel,
   * then tries to advance.
   *
   *   laborForce      bodies dedicated to establishment (workers + specialists).
   *   foodThroughput  food delivered home since the last step — only matters on
   *                   the FoodTransport rung, where it caps progress so the
   *                   Settled→Established step genuinely needs food FLOWING.
   *
   * Returns the number of stages advanced this step. Deterministic, no allocation.
   */
  investLabor(cid: ColonyId, mgr: RegionManager, laborForce: number, foodThroughput: number): number {
    this.syncExploration(cid, mgr);
    const r = this.activeTarget(cid);

    // The region that received effort this step (NIL if none) — it alone has its
    // idle clock reset; every other ladder region accrues toward abandonment.
    let fedRegion = NIL;
    let advanced = 0;
    if (r >= 0) {
      const c = this.cell(cid, r);
      const work = STAGE_WORK[this.stage[c] as number] as number;
      // Base effort scales with the dedicated labor force (always ≥ 1 if any labor).
      const base = laborForce <= 0 ? 0 : 1 + Math.floor(laborForce / LABOR_DIVISOR);
      let amount = base;
      if (work === ESTABLISH_WORK.FoodTransport) {
        // Establishing a supply line is bounded by the food actually moving on it.
        const flow = foodThroughput <= 0 ? 0 : 1 + Math.floor(foodThroughput / THROUGHPUT_DIVISOR);
        amount = Math.min(base, flow);
      }
      if (amount > 0) {
        const idx = c * ESTABLISH_WORK_COUNT + work;
        this.effort[idx] = (this.effort[idx] as number) + amount;
        fedRegion = r;
        advanced = this.tryAdvance(cid, r);
      }
    }

    // Abandonment heartbeat: bleed/demote every region of this colony that went
    // unfed this step. No-op (and untouched state) unless decay is enabled, so the
    // default is byte-identical. EXCEPTION: a fully-settled realm (no frontier left
    // to build, `r < 0`) that still has labor to spend is NOT abandoning anything —
    // skip the heartbeat so completed territory under active upkeep never crumbles
    // for lack of further work. Genuine neglect (labor absent, or labor directed at
    // a DIFFERENT frontier) still decays.
    if (!(r < 0 && laborForce > 0)) {
      this.decayStep(cid, fedRegion);
    }
    return advanced;
  }

  // --- Accessors (integer, O(1)) --------------------------------------------

  /** Colony `cid`'s current establishment stage for region `r` (TERRITORY_STAGE.*). */
  stageOf(cid: ColonyId, r: number): number {
    return this.stage[this.cell(cid, r)] as number;
  }

  /** Banked effort in one of colony `cid`'s region work channels. */
  effortOf(cid: ColonyId, r: number, work: number): number {
    return this.effort[this.effortIdx(cid, r, work)] as number;
  }

  /** Percent of base resource amount colony `cid` currently delivers from `r` (stage table). */
  yieldPctOf(cid: ColonyId, r: number): number {
    return STAGE_YIELD_PCT[this.stage[this.cell(cid, r)] as number] as number;
  }

  /** True once colony `cid`'s stage for `r` unlocks gene drops (off until Settled). */
  hasGeneAccess(cid: ColonyId, r: number): boolean {
    return (STAGE_GENE_ACCESS[this.stage[this.cell(cid, r)] as number] as number) === 1;
  }

  /** Rarity-bias delta colony `cid`'s stage for `r` contributes (added to the region's base bias). */
  rarityDeltaOf(cid: ColonyId, r: number): number {
    return STAGE_RARITY_DELTA[this.stage[this.cell(cid, r)] as number] as number;
  }

  /** Effective food colony `cid` delivers home from a node worth `baseAmount` in `r`. */
  effectiveFoodAmount(cid: ColonyId, r: number, baseAmount: number): number {
    return Math.floor((baseAmount * this.yieldPctOf(cid, r)) / 100);
  }

  /** Effective gene rarity bias for colony `cid` in `r` = region base bias + stage delta (≥ 0). */
  effectiveRarityBias(cid: ColonyId, r: number, baseBias: number): number {
    return Math.max(0, baseBias + this.rarityDeltaOf(cid, r));
  }

  /**
   * Percent progress toward colony `cid` leaving region `r`'s current stage
   * (0..100). Returns 100 for a fully Integrated province and 0 for a region not
   * yet on the ladder.
   */
  progressPctToNext(cid: ColonyId, r: number): number {
    const c = this.cell(cid, r);
    const s = this.stage[c] as number;
    if (s < TERRITORY_STAGE.Explored || s >= TERRITORY_STAGE.Integrated) {
      return s >= TERRITORY_STAGE.Integrated ? 100 : 0;
    }
    const work = STAGE_WORK[s] as number;
    const need = STAGE_REQUIREMENT[s] as number;
    const have = this.effort[c * ESTABLISH_WORK_COUNT + work] as number;
    return Math.min(100, Math.floor((100 * have) / need));
  }

  /** Regions colony `cid` carries food home from at full yield or better (stage ≥ Established). */
  establishedCount(cid: ColonyId): number {
    let n = 0;
    const base = cid * REGION_COUNT;
    for (let r = 0; r < REGION_COUNT; r++) {
      if ((this.stage[base + r] as number) >= TERRITORY_STAGE.Established) n++;
    }
    return n;
  }

  /** Fully-absorbed provinces for colony `cid` (stage === Integrated). */
  integratedCount(cid: ColonyId): number {
    let n = 0;
    const base = cid * REGION_COUNT;
    for (let r = 0; r < REGION_COUNT; r++) {
      if ((this.stage[base + r] as number) === TERRITORY_STAGE.Integrated) n++;
    }
    return n;
  }

  // --- Determinism fingerprint ----------------------------------------------

  /**
   * FNV-1a fingerprint of the FULL establishment state across every colony (fold
   * into a world hash). Folds in a fixed (cid, region) order — the whole stage
   * plane, then the whole effort plane — then the global advancement total, so
   * the same sequence of per-colony investments always yields the same hash.
   */
  hash(): number {
    let h = fnvInit();
    const cells = this.maxColonies * REGION_COUNT;
    for (let i = 0; i < cells; i++) h = fnvU32(h, this.stage[i] as number);
    for (let i = 0; i < cells * ESTABLISH_WORK_COUNT; i++) {
      h = fnvU32(h, this.effort[i] as number);
    }
    h = fnvU32(h, this.advancementsTotal);
    return h >>> 0;
  }
}

/** Build a fresh TerritorySystem sized for `maxColonies` (parallels newRegionManager).
 *  Pass a decay config (e.g. `SLOW_TERRITORY_DECAY`) to enable abandonment demotion;
 *  the default is inert. */
export function newTerritorySystem(
  maxColonies: number,
  decay: TerritoryDecayConfig = DEFAULT_TERRITORY_DECAY,
): TerritorySystem {
  return new TerritorySystem(maxColonies, decay);
}
